import { useCallback, useEffect, useState } from "react";
import { ticketService } from "../services/ticketService";

type TicketComment = {
  id: number;
  content: string;
  user?: { id?: number; name?: string };
  created_at?: string;
};

export function useTicketComments(ticketId?: number | null) {
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    if (ticketId == null || Number.isNaN(ticketId)) {
      setComments([]);
      setError("Identifiant de ticket invalide.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await ticketService.getTicketById(ticketId);
      setComments(response.data?.comments ?? []);
    } catch (err: any) {
      setError(err?.message ?? "Impossible de charger les commentaires.");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  const sendComment = useCallback(
    async (comment: string, userId: number) => {
      if (ticketId == null || Number.isNaN(ticketId)) {
        throw new Error("Identifiant de ticket invalide.");
      }
      if (!userId) {
        throw new Error("Utilisateur non authentifié.");
      }

      try {
        setSending(true);
        setSendError(null);
        // backend expects payload { comment: string, user_id: number }
        await ticketService.createComment(ticketId, { comment, user_id: userId });
        await fetchComments();
      } catch (err: any) {
        const message = err?.message ?? "Impossible d'envoyer le commentaire.";
        setSendError(message);
        throw new Error(message);
      } finally {
        setSending(false);
      }
    },
    [ticketId, fetchComments]
  );

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return {
    comments,
    loading,
    error,
    sending,
    sendError,
    sendComment,
    reload: fetchComments,
  };
}